// ---------------------------------------------- modules import
import M from "materialize-css";
import React, { FunctionComponent, useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import ResetPassword from "./resetPassword";

import { IDetailsProps } from "./common";
import { useAuth, useUser } from "./hooks";
import { ThemeContext } from "../../../contexts/themeContext";
import PermissionHelper, {
  MenuIndex
} from "../../../store/helpers/permissionHelper";
import TagHelper from "../../../store/helpers/tagHelper";

// ---------------------------------------------- the component
const Details: FunctionComponent<IDetailsProps> = ({ menuIndex }) => {
  // ---------------------------------------------- router state
  const { user_id } = useParams();

  // ---------------------------------------------- local state
  const { auth, profile } = useAuth();
  const {
    loading,
    error,
    user,
    isResetReady,
    isNewPasswordValid,
    onChangePassword,
    onToggleReset,
    onUpdatePassword,
    onDelete
  } = useUser(menuIndex, user_id);

  const { id, name, email, permissions } = user;

  const tags = TagHelper.toTags(user.tags);
  const menuGroups = PermissionHelper.toMenuGroups(permissions);

  // ---------------------------------------------- handlers
  const handleUpdatePassword = () => onUpdatePassword(profile);

  const handleDelete = () =>
    onDelete(auth, profile, `/${PermissionHelper.getUrl(MenuIndex.User)}`);

  // ---------------------------------------------- effects
  useEffect(() => {
    const collapsibles = document.querySelectorAll(".collapsible");

    if (collapsibles) M.Collapsible.init(collapsibles);
  }, []);

  useEffect(() => {
    const modals = document.querySelectorAll(".modal");

    M.Modal.init(modals);
  }, []);

  // ---------------------------------------------- content
  return (
    <ThemeContext.Consumer>
      {({ theme }) => (
        <div className="container row">
          <div className="card col s12 m8 offset-m2">
            <div className="card-content" style={{ paddingBottom: "0px" }}>
              <span style={{ fontSize: "0.75rem" }}>
                {PermissionHelper.getName(MenuIndex.User).toLowerCase()}
              </span>

              <span
                className="new badge"
                style={{ backgroundColor: theme.ui, color: theme.syntax }}
                data-badge-caption={email}
              />

              <span className="card-title">{name.toUpperCase()}</span>

              <ul className="collapsible" style={{ marginTop: "20px" }}>
                <li>
                  <div className="collapsible-header waves-effect">
                    <i className="material-icons" style={{ color: theme.icon }}>
                      local_offer
                    </i>
                    Tags
                  </div>

                  <div
                    className="collapsible-body"
                    style={{ fontSize: "0.7em" }}
                  >
                    <table>
                      <tbody style={{ paddingLeft: "1rem" }}>
                        <tr>
                          <td className="center-align">Tag</td>

                          <td className="center-align">Tagged</td>
                        </tr>

                        {tags.map(tag => (
                          <tr key={tag.index}>
                            <td>{tag.shortName}</td>

                            <td className="center-align">
                              {tag.tagged ? (
                                <i className="tiny material-icons green-text">
                                  check
                                </i>
                              ) : (
                                <i className="tiny material-icons red-text">
                                  clear
                                </i>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </li>
              </ul>

              <ul className="collapsible" style={{ marginTop: "20px" }}>
                {menuGroups.map((menuGroup, index) => (
                  <li key={index}>
                    <div className="collapsible-header waves-effect">
                      <i
                        className="material-icons"
                        style={{ color: theme.icon }}
                      >
                        {menuGroup.icon}
                      </i>
                      {menuGroup.name}
                    </div>

                    <div
                      className="collapsible-body"
                      style={{ fontSize: "0.7em" }}
                    >
                      <table>
                        <tbody style={{ paddingLeft: "1rem" }}>
                          <tr>
                            <td className="center-align">{menuGroup.name}</td>

                            <td className="center-align">View</td>

                            <td className="center-align">Modify</td>
                          </tr>

                          {menuGroup.menus.map((menu, index) => (
                            <tr key={index}>
                              <td>{menu.shortName}</td>

                              <td className="center-align">
                                {menu.permission.view ? (
                                  <i className="tiny material-icons green-text">
                                    check
                                  </i>
                                ) : (
                                  <i className="tiny material-icons red-text">
                                    clear
                                  </i>
                                )}
                              </td>

                              <td className="center-align">
                                {menu.permission.modify ? (
                                  <i className="tiny material-icons green-text">
                                    check
                                  </i>
                                ) : (
                                  <i className="tiny material-icons red-text">
                                    clear
                                  </i>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card-action row" style={{ marginBottom: "0" }}>
              <div className="col s3">
                <Link
                  to={`/${PermissionHelper.getUrl(MenuIndex.User)}`}
                  className="btn-floating btn-small waves-effect waves-light"
                  style={{ backgroundColor: theme.ui }}
                >
                  <i className="material-icons left">arrow_back</i>
                </Link>
              </div>

              {profile &&
              PermissionHelper.getModify(profile.permissions, MenuIndex.User) &&
              auth.uid !== id ? (
                <div className="col s9 right-align">
                  <Link
                    to={`/${PermissionHelper.getUrl(
                      MenuIndex.User
                    )}/update/${id}`}
                    className="btn-floating btn-small waves-effect waves-light"
                    style={{ backgroundColor: theme.ui, margin: "0 5px" }}
                  >
                    <i className="tiny material-icons">edit</i>
                  </Link>

                  <button
                    type="button"
                    className="btn-floating btn-small waves-effect waves-light"
                    style={{ backgroundColor: theme.ui, margin: "0 5px" }}
                    onClick={() => onToggleReset()}
                  >
                    <i className="tiny material-icons">lock</i>
                  </button>

                  <button
                    type="button"
                    className="btn-floating btn-small waves-effect waves-light modal-trigger"
                    data-target="alert"
                    style={{ backgroundColor: theme.ui, marginLeft: "5px" }}
                  >
                    <i className="tiny material-icons">delete</i>
                  </button>

                  <div className="modal" id="alert">
                    <div className="modal-content center">
                      <h5>Confirm Delete</h5>

                      <p>{`Are you sure you want to delete ${name} from the list?`}</p>
                    </div>

                    <div className="modal-footer">
                      <div className="container">
                        <button
                          type="button"
                          className="btn modal-close"
                          style={{ backgroundColor: theme.ui }}
                          onClick={handleDelete}
                        >
                          Yes, I'm sure
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div
            className={`col s12 m6 offset-m3 center-align ${
              error ? "red-text" : null
            }`}
            style={{ marginTop: "2rem" }}
          >
            {loading ? "Resetting password ..." : error ? error : null}
          </div>

          {isResetReady ? (
            <ResetPassword
              isNewPasswordValid={isNewPasswordValid}
              onChangePassword={onChangePassword}
              onUpdatePassword={handleUpdatePassword}
            />
          ) : null}
        </div>
      )}
    </ThemeContext.Consumer>
  );
};

export default Details;
